import { useState } from "react";
import Calendar from "react-calendar";
import { isSameDay } from "~/utils/belt";
import type { TournamentProps } from "~/helpers/prepare-tournament-data";
import DateTournaments from "./DateTournaments";

type Detail = "century" | "decade" | "year" | "month";

const TitleContent: React.FC<{
  activeStartDate: Date;
  date: Date;
  view: Detail;
  hasTournament?: boolean;
}> = ({ date, hasTournament }) => {
  const today = new Date();
  return (
    <div
      className="flex flex-col items-center justify-start h-12"
      style={{ opacity: isSameDay(date, today) || date > today ? 1 : 0.6 }}
    >
      <h4>{date.getDate()}</h4>
      {hasTournament ? (
        <em className="h-2 w-2 bg-jiwe-orange dark:bg-jiwe-pink rounded" />
      ) : (
        <em className="h-2 w-2 rounded bg-[transparent]" />
      )}
    </div>
  );
};

const tileClassName =
  (datesToAddClassTo: Date[]) =>
  ({ date, view }: { date: Date; view: Detail }) => {
    // Add class to tiles in month view only
    if (view === "month") {
      // Check if a date React-Calendar wants to check is on the list of dates to add class to
      if (datesToAddClassTo.find((dDate) => isSameDay(dDate, date))) {
        return "text-jiwe-red dark:text-jiwe-pink";
      }
    }
    const today = new Date();

    if (!isSameDay(date, today) && date < today) {
      return "opacity-60 dark:text-grey-dark";
    }
    return "dark:text-grey-dark";
  };

const TournamentsCalendar: React.FC<{
  tournaments: TournamentProps[];
  daysTournaments: any;
  tournamentDates: Date[];
}> = ({ tournamentDates, daysTournaments }) => {
  const [selectedDate, setValue] = useState<Date>(new Date());

  return (
    <div>
      <h2 className="text-grey-dark dark:text-jiwe-blue text-base uppercase font-bold mb-4">
        Tournaments Calendar
      </h2>
      <div className="w-full flex flex-col lg:flex-row gap-x-8">
        <div className="min-w-[22.5rem] lg:w-[27.375rem]">
          <Calendar
            onChange={(clickedDate) => {
              if (clickedDate instanceof Date) {
                setValue(clickedDate);
              }
            }}
            value={selectedDate}
            tileClassName={tileClassName(tournamentDates)}
            minDetail="year"
            prevLabel={null}
            nextLabel={null}
            prev2Label={
              <span className="">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="9.414"
                  height="16.828"
                  viewBox="0 0 9.414 16.828"
                >
                  <path
                    id="Icon_feather-chevron-right"
                    data-name="Icon feather-chevron-right"
                    d="M20.5,23l-7-7,7-7"
                    transform="translate(-12.5 -7.586)"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    className="dark:stroke-grey-dark stroke-grey-darker"
                  />
                </svg>
              </span>
            }
            next2Label={
              <span className="">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="9.414"
                  height="16.828"
                  viewBox="0 0 9.414 16.828"
                >
                  <path
                    id="Icon_feather-chevron-right"
                    data-name="Icon feather-chevron-right"
                    d="M13.5,23l7-7-7-7"
                    transform="translate(-12.086 -7.586)"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    className="dark:stroke-grey-dark stroke-grey-darker"
                  />
                </svg>
              </span>
            }
            tileContent={({ activeStartDate, date, view }) => (
              <TitleContent
                activeStartDate={activeStartDate}
                date={date}
                view={view}
                hasTournament={
                  tournamentDates
                    .map((dDate) => isSameDay(dDate, date))
                    .filter((el) => !!el).length > 0
                }
              />
            )}
          />
        </div>
        <DateTournaments
          daysTournaments={daysTournaments}
          selectedDate={selectedDate}
        />
      </div>
    </div>
  );
};

export default TournamentsCalendar;
